import { SLUGS, DEFAULT_ORIGIN, resolveSlug } from "../../shared/howRelayWorks";

// Spans that must pass through untouched: fenced code, inline code, and
// existing markdown links / images.
const PROTECTED = /```[\s\S]*?(?:```|$)|`[^`\n]*`|!?\[[^\]\n]*\]\([^)\n]*\)/g;

const ORIGIN = DEFAULT_ORIGIN.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// A bare "/#slug" or "<origin>/#slug", not glued to a preceding word/path.
const BARE = new RegExp(`(^|[^\\w/#.:-])((?:${ORIGIN})?/#([a-z0-9-]+))(?![\\w-])`, "g");

/** Matches the href written by linkifyHowRelayWorks; used to render it in-app. */
export function howRelayWorksSlugFromHref(href: string | undefined): string | null {
  const m = href ? new RegExp(`^(?:${ORIGIN})?/#([a-z0-9-]+)$`).exec(href) : null;
  return m && SLUGS.includes(resolveSlug(m[1])) ? resolveSlug(m[1]) : null;
}

export type HowRelayWorksSegment = string | { slug: string; label: string };

function segmentPlain(text: string, out: HowRelayWorksSegment[]): void {
  let last = 0;
  for (const m of text.matchAll(BARE)) {
    const [whole, pre, label, slug] = m;
    if (!SLUGS.includes(resolveSlug(slug))) continue;
    const start = m.index + pre.length;
    if (start > last) out.push(text.slice(last, start));
    out.push({ slug: resolveSlug(slug), label });
    last = m.index + whole.length;
  }
  if (last < text.length) out.push(text.slice(last));
}

/**
 * Split text into plain strings and How Relay Works links (bare /#slug or
 * easyrelay.app/#slug for known slugs). Code spans, code blocks and existing
 * markdown links stay in the plain strings.
 */
export function segmentHowRelayWorks(text: string): HowRelayWorksSegment[] {
  const out: HowRelayWorksSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(PROTECTED)) {
    segmentPlain(text.slice(last, m.index), out);
    out.push(m[0]);
    last = m.index + m[0].length;
  }
  segmentPlain(text.slice(last), out);
  return out;
}

/** Turn bare /#slug (and easyrelay.app/#slug) into markdown links for known slugs. */
export function linkifyHowRelayWorks(text: string): string {
  return segmentHowRelayWorks(text)
    .map((seg) => (typeof seg === "string" ? seg : `[${seg.label}](/#${seg.slug})`))
    .join("");
}
